/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import OrderDetailsOrderLine from "@eisinc/App/Components/OrderDetails/OrderDetailsOrderLine";
import { spinnerStyle } from "@eisinc/App/SharedStyles/GeneralStyles";
import { OrderDetailsTableStyle } from "@eisinc/App/SharedStyles/OrderDetailsStyles";
import { OrderDetailsPageContext } from "@eisinc/Overrides/Pages/OrderDetailsPage";
import printStyles, { onPrint } from "@eisinc/XDL/Utilities/print";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable from "@insite/mobius/DataTable/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    order: getOrderState(state, state.pages.orderDetails.orderNumber),
    wishListSettings: getSettingsCollection(state).wishListSettings,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    loadProductInfoList,
};

export interface OrderDetailSummaryTableStyles {
    firstTableHeader: DataTableHeaderProps;
    tableHeader: DataTableHeaderProps;
    printStyle?: InjectableCss;
}

export const orderDetailsSummaryTable: OrderDetailSummaryTableStyles = {
    firstTableHeader: {
        css: css`
            width: 35%;

            ${onPrint(css`
                width: 40%;

                &.first-table-header {
                    display: flex;
                    align-items: center;
                }
            `)}
        `,
    },
    tableHeader: {
        css: css`
            width: calc(65% / 5);

            &.hide-print {
                ${printStyles.hideOnPrint}
            }
            &.quote-print {
                ${onPrint(css`
                    width: calc(65% / 4);

                    * {
                        color: transparent;
                    }
                `)}
            }
        `,
    },
    printStyle: {
        css: css`
            ${printStyles.hideOnPrint}
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const OrderDetailsSummaryTableV2: React.FunctionComponent<Props> = ({
    order,
    wishListSettings,
    loadProductInfoList,
}) => {
    const styles = mergeToNew(OrderDetailsTableStyle, orderDetailsSummaryTable);

    if (!order.value || !order.value.orderLines) {
        return (
            <StyledWrapper {...spinnerStyle.centeringWrapper}>
                <LoadingSpinner {...spinnerStyle.spinner} />
            </StyledWrapper>
        );
    }

    useEffect(() => {
        const cartLines = order.value.orderLines;
        const productIds = [] as any;

        cartLines
            ?.filter((line: any) => {
                return !!line.productId;
            })
            // eslint-disable-next-line array-callback-return
            .map((line: any) => {
                if (productIds.indexOf(line.productId) < 0) {
                    productIds.push(line.productId);
                }
            });

        if (productIds && productIds.length) {
            loadProductInfoList({
                id: `orderDetails`,
                getProductCollectionParameter: { productIds },
            });
        }
    });

    const orderLines = React.useMemo(() => {
        return [...(order?.value?.orderLines ?? [])].sort((a, b) => a.lineNumber - b.lineNumber);
    }, [order]);

    const headerColumns = [
        `${translate("Product")}`,
        `${translate("Order Status")}`,
        `${translate("Quantities")}`,
        `${translate("Pricing")}`,
        `${translate("Actions")}`,
        ``,
    ];
    const numberOfColumn = headerColumns.length;

    return (
        <StyledWrapper {...styles.printStyle}>
            <StyledWrapper {...styles.tableWarpper}>
                <Typography {...styles.infoLabel}>{translate("Product Information")}</Typography>
                <DataTable>
                    <DataTableHead {...styles.tableHeadStyle}>
                        {headerColumns.map((column, index) => (
                            <DataTableHeader
                                //  eslint-disable-next-line react/no-array-index-key
                                key={`${column}_${index}`}
                                {...(index === 0 ? styles.firstTableHeader : styles.tableHeader)}
                            >
                                {column}
                            </DataTableHeader>
                        ))}
                    </DataTableHead>
                    <DataTableBody {...styles.tableBodyStyle}>
                        {orderLines?.map(orderLine => (
                            <OrderDetailsOrderLine
                                key={orderLine.id}
                                headerColumns={headerColumns}
                                orderLine={orderLine}
                                wishListSettings={wishListSettings}
                                numberOfColumn={numberOfColumn}
                            />
                        ))}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderDetailsSummaryTableV2),
    definition: {
        displayName: "Order Details Summary Table V2",
        allowedContexts: [OrderDetailsPageContext],
        group: "Order Details",
    },
};

export default widgetModule;
